"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import type { DeleteSituationsRequest, Situation } from "@/generated/fastapi";

export type RelatedThoughtsDeleteAction = NonNullable<
  DeleteSituationsRequest["related_thoughts_action"]
>;

export type RelatedThoughtsDeleteChoice = {
  action: RelatedThoughtsDeleteAction;
  targetSituationId?: string | null;
};

const DEFAULT_RELATED_THOUGHTS_DELETE_CHOICE: RelatedThoughtsDeleteChoice = {
  action: "remove_anchor",
  targetSituationId: null,
};

type StudioDeletionStateContextValue = {
  relatedThoughtsDeleteChoice: RelatedThoughtsDeleteChoice;
  setRelatedThoughtsDeleteChoice: Dispatch<
    SetStateAction<RelatedThoughtsDeleteChoice>
  >;
  anchorThoughtsToSituation: Situation[];
  setAnchorThoughtsToSituation: Dispatch<SetStateAction<Situation[]>>;
  pendingSituationDeletionRequest: DeleteSituationsRequest | null;
  setPendingSituationDeletionRequest: Dispatch<
    SetStateAction<DeleteSituationsRequest | null>
  >;
  resetRelatedThoughtsDeleteChoice: () => void;
};

const StudioDeletionStateContext = createContext<
  StudioDeletionStateContextValue | undefined
>(undefined);

export function StudioDeletionStateProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [relatedThoughtsDeleteChoice, setRelatedThoughtsDeleteChoice] =
    useState<RelatedThoughtsDeleteChoice>(
      DEFAULT_RELATED_THOUGHTS_DELETE_CHOICE,
    );
  const [anchorThoughtsToSituation, setAnchorThoughtsToSituation] = useState<
    Situation[]
  >([]);
  const [pendingSituationDeletionRequest, setPendingSituationDeletionRequest] =
    useState<DeleteSituationsRequest | null>(null);

  const resetRelatedThoughtsDeleteChoice = useCallback(() => {
    setRelatedThoughtsDeleteChoice(DEFAULT_RELATED_THOUGHTS_DELETE_CHOICE);
    setAnchorThoughtsToSituation([]);
  }, []);

  const value = useMemo(
    () => ({
      relatedThoughtsDeleteChoice,
      setRelatedThoughtsDeleteChoice,
      anchorThoughtsToSituation,
      setAnchorThoughtsToSituation,
      pendingSituationDeletionRequest,
      setPendingSituationDeletionRequest,
      resetRelatedThoughtsDeleteChoice,
    }),
    [
      anchorThoughtsToSituation,
      pendingSituationDeletionRequest,
      relatedThoughtsDeleteChoice,
      resetRelatedThoughtsDeleteChoice,
    ],
  );

  return (
    <StudioDeletionStateContext.Provider value={value}>
      {children}
    </StudioDeletionStateContext.Provider>
  );
}

export function useStudioDeletionStateContext() {
  const context = useContext(StudioDeletionStateContext);

  if (context === undefined) {
    throw new Error(
      "useStudioDeletionStateContext must be used within a StudioDeletionStateProvider",
    );
  }

  return context;
}
